// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { CalendarClock, Save } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { CodeDialectBadge } from "@/components/shared/code-dialect-badge";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { validateExpression } from "@/lib/expression-validator";
import { cn } from "@/lib/utils";
import { type OpeningHoursEntry, openHoursFor } from "./opening-hours";
import { ScheduleEditor } from "./schedule-editor";
import { type CaptureWindow, scheduleOverlaps } from "./schedule-utils";
import type { ScheduleException, ScheduleSettings } from "./types";

// Variables available to schedule rule expressions (evaluated with Python's eval).
const SCHEDULE_RULE_VARIABLES = [
  "month",
  "day_of_month",
  "day_of_week",
  "tag_names",
  "camera_type",
  "processing_types",
];

interface ScheduleExceptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exception: ScheduleException | null;
  baseSettings: ScheduleSettings;
  window?: CaptureWindow;
  /** Per-day opening hours, resolved against the rule as it's typed. */
  openingHours: OpeningHoursEntry[];
  /** The store's usual hours, used when the rule's days don't resolve to one window. */
  fallbackOpenWindow?: CaptureWindow;
  onSave: (exception: ScheduleException) => void;
}

export function ScheduleExceptionDialog({
  open,
  onOpenChange,
  exception,
  baseSettings,
  window,
  openingHours,
  fallbackOpenWindow,
  onSave,
}: ScheduleExceptionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-[57rem]">
        {open && (
          <ExceptionForm
            key={exception?.id ?? "new"}
            exception={exception}
            baseSettings={baseSettings}
            window={window}
            openingHours={openingHours}
            fallbackOpenWindow={fallbackOpenWindow}
            onCancel={() => onOpenChange(false)}
            onSave={(ex) => {
              onSave(ex);
              onOpenChange(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ExceptionFormProps {
  exception: ScheduleException | null;
  baseSettings: ScheduleSettings;
  window?: CaptureWindow;
  openingHours: OpeningHoursEntry[];
  fallbackOpenWindow?: CaptureWindow;
  onCancel: () => void;
  onSave: (exception: ScheduleException) => void;
}

function ExceptionForm({
  exception,
  baseSettings,
  window,
  openingHours,
  fallbackOpenWindow,
  onCancel,
  onSave,
}: ExceptionFormProps) {
  const { t } = useTranslation("back-office");
  const [name, setName] = useState(() => exception?.name ?? "");
  const [tickets, setTickets] = useState<string[]>(() => structuredClone(exception?.tickets ?? []));
  const [expression, setExpression] = useState(() => exception?.expression ?? "");
  const [definition, setDefinition] = useState<ScheduleSettings>(() =>
    structuredClone(exception?.definition ?? baseSettings),
  );

  const trimmedExpression = expression.trim();
  const ruleResult = validateExpression(trimmedExpression, "python", SCHEDULE_RULE_VARIABLES);
  const ruleInvalid = trimmedExpression.length > 0 && !ruleResult.valid;
  // Overlapping windows are flagged inline by the editor; saving them would silently drop captures.
  const hasOverlaps = scheduleOverlaps(definition).length > 0;
  // Only a syntactically valid rule is worth reading days out of — a half-typed one would keep
  // resolving to a different day set on every keystroke.
  const openHours = openHoursFor(openingHours, ruleInvalid ? undefined : trimmedExpression, fallbackOpenWindow);

  function save() {
    onSave({
      id: exception?.id ?? crypto.randomUUID(),
      name: name.trim() || undefined,
      tickets: tickets.length ? tickets : undefined,
      expression: expression.trim() || undefined,
      definition,
    });
  }

  return (
    <>
      <div className="flex">
        <WizardPane icon={CalendarClock} />
        <div className="flex min-w-0 flex-1 flex-col min-h-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>
              {exception ? t("stores.schedule.exception.editTitle") : t("stores.schedule.exception.addTitle")}
            </DialogTitle>
            <DialogDescription>{t("stores.schedule.exception.subtitle")}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 overflow-y-auto max-h-[60vh] p-6 pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="exception-name">{t("stores.schedule.exception.name")}</Label>
                <Input id="exception-name" value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
              </div>
              <CsvInput label={t("stores.schedule.exception.tickets")} values={tickets} onChange={setTickets} />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="exception-rule">{t("stores.schedule.exception.rule")}</Label>
                <CodeDialectBadge dialect="python" />
              </div>
              <Textarea
                id="exception-rule"
                value={expression}
                onChange={(e) => setExpression(e.target.value)}
                rows={2}
                className={cn(
                  "min-h-14 break-words font-mono",
                  ruleInvalid && "border-destructive focus-visible:ring-destructive/40",
                )}
              />
              {ruleInvalid && (
                <p className="text-sm text-destructive">
                  {t("stores.schedule.exception.ruleInvalid")}
                  {ruleResult.error ? ` — ${ruleResult.error}` : ""}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <span>{t("stores.schedule.exception.variables")}:</span>
              {[...SCHEDULE_RULE_VARIABLES].sort().map((variable) => (
                <code key={variable} className="rounded bg-muted px-1.5 py-0.5 font-mono">
                  {variable}
                </code>
              ))}
            </div>

            <Separator />

            <div className="space-y-2">
              <h4 className="text-sm font-medium">{t("stores.schedule.exception.scheduleHeading")}</h4>
              <ScheduleEditor
                settings={definition}
                onChange={setDefinition}
                editable
                window={window}
                openHours={openHours}
                onCopyBase={() => setDefinition(structuredClone(baseSettings))}
              />
            </div>
          </div>
        </div>
      </div>

      <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("buttons.cancel")}
        </Button>
        <Button type="button" disabled={!trimmedExpression || ruleInvalid || hasOverlaps} onClick={save}>
          <Save className="h-4 w-4" />
          {t("buttons.save")}
        </Button>
      </DialogFooter>
    </>
  );
}

function CsvInput({
  label,
  values,
  onChange,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        defaultValue={values.join(", ")}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
        className="h-9"
      />
    </div>
  );
}
